import {
	attrsEqual,
	type JSONContent,
	type MarkdownParseHelpers,
	type MarkdownRendererHelpers,
	type MarkdownToken,
	Node,
	type RenderContext,
	renderNestedMarkdownContent,
} from "@tiptap/core";
import Blockquote from "@tiptap/extension-blockquote";
import Heading from "@tiptap/extension-heading";
import BaseItalic from "@tiptap/extension-italic";
import Link from "@tiptap/extension-link";
import { BulletList as BaseBulletList, OrderedList as BaseOrderedList } from "@tiptap/extension-list";
import BaseListItem from "@tiptap/extension-list-item";
import Paragraph from "@tiptap/extension-paragraph";
import { MarkdownManager } from "@tiptap/markdown";
import StarterKit from "@tiptap/starter-kit";
import { boxIal, Callout, contractTests, shortNames, withContainer } from "./callout";
import { attachIals, BlockIal, prepare, restoreColons, unprepare, withIal } from "./ials";
import { linksToAnchors, siteLink } from "./links";
import { expandMap, mapArgs } from "./map";
import { canonicalTable } from "./table";
import { tableNode } from "./table_model";
import { tableExtensions } from "./table_nodes";

const INLINE_TAGS = [
	"ESTAGIO",
	"MONSTRO",
	"MAGIA",
	"EQUIPAMENTO",
	"CAPITULO",
	"LIVRO",
	"REDBOX",
	"BLACKBOX",
	"BLUEBOX",
	"YOUTUBE",
	"CHAPTER_ASSET_PATH",
	"ICON_ASSET_PATH",
];
const BOX_TAGS = ["MAPA", "MONSTERBOX", "SPELLBOX", "CHARACTERBOX", "RETAINERBOX"];
const INLINE_TAG = new RegExp(`^(${INLINE_TAGS.join("|")})\\[([^\\]\\n]*)\\]`);
const BOX_TAG = new RegExp(`^(${BOX_TAGS.join("|")})\\[([^\\]\\n]*)\\][ \\t]*(?:\\n|$)`);
const BLOCK_HTML_TAGS = "div|table|blockquote|ol|ul|dl|p|h[1-6]";

// Raw HTML element starting at src[0], up to its matching close tag (same name, nesting counted).
function matchElement(src: string, tag: string): number {
	const re = new RegExp(`<(/?)${tag}\\b[^>]*?(/?)>`, "gi");
	let depth = 0;
	for (let m = re.exec(src); m; m = re.exec(src)) {
		if (m[2] === "/") {
			if (depth === 0) return m.index + m[0].length;
			continue;
		}
		depth += m[1] ? -1 : 1;
		if (depth === 0) return m.index + m[0].length;
	}
	return -1;
}

const OdoTag = Node.create({
	name: "odoTag",
	group: "inline",
	inline: true,
	atom: true,
	addAttributes: () => ({ tag: { default: "" }, args: { default: "" } }),
	renderHTML: ({ node }) => ["span", { "data-odo-tag": node.attrs.tag }, `${node.attrs.tag}[${node.attrs.args}]`],
	markdownTokenizer: {
		name: "odoTag",
		level: "inline",
		start: (src: string) => src.search(new RegExp(`(${INLINE_TAGS.join("|")})\\[`)),
		tokenize: (src: string) => {
			const m = INLINE_TAG.exec(src);
			if (!m) return undefined;
			const link = siteLink(m[1], m[2]);
			if (link) return { type: "link", raw: m[0], href: link.href, title: null, text: link.text, tokens: [{ type: "text", raw: link.text, text: link.text }] };
			return { type: "odoTag", raw: m[0], tag: m[1], args: m[2] };
		},
	},
	parseMarkdown: (token: MarkdownToken): JSONContent => ({ type: "odoTag", attrs: { tag: token.tag, args: token.args } }),
	renderMarkdown: (node: JSONContent) => `${node.attrs.tag}[${node.attrs.args}]`,
});

const OdoBox = Node.create({
	name: "odoBox",
	group: "block",
	atom: true,
	addAttributes: () => ({ tag: { default: "" }, args: { default: "" } }),
	renderHTML: ({ node }) => ["div", { "data-odo-box": node.attrs.tag }, `${node.attrs.tag}[${node.attrs.args}]`],
	markdownTokenizer: {
		name: "odoBox",
		level: "block",
		start: (src: string) => src.search(new RegExp(`\\n(${BOX_TAGS.join("|")})\\[`)),
		tokenize: (src: string) => {
			const m = BOX_TAG.exec(src);
			return m ? { type: "odoBox", raw: m[0], tag: m[1], args: m[2] } : undefined;
		},
	},
	parseMarkdown: (token: MarkdownToken) => ({ type: "odoBox", attrs: { tag: token.tag, args: token.args } }),
	renderMarkdown: (node: JSONContent) => `${node.attrs.tag}[${node.attrs.args}]`,
});

const RawHtmlBlock = Node.create({
	name: "rawHtmlBlock",
	group: "block",
	atom: true,
	addAttributes: () => ({ raw: { default: "" }, ial: { default: null, rendered: false } }),
	renderHTML: ({ node }) => ["div", { "data-raw-html": "" }, node.attrs.raw],
	markdownTokenizer: {
		name: "rawHtmlBlock",
		level: "block",
		start: (src: string) => src.search(new RegExp(`\\n {0,3}<(${BLOCK_HTML_TAGS}|br|hr)\\b`, "i")),
		tokenize: (src: string) => {
			const voidLine = /^ {0,3}<(?:br|hr)\b[^>]*>[ \t]*(?:\n|$)/i.exec(src);
			if (voidLine) return { type: "rawHtmlBlock", raw: voidLine[0], html: voidLine[0].replace(/\n$/, "") };
			const open = new RegExp(`^ {0,3}<(${BLOCK_HTML_TAGS})\\b`, "i").exec(src);
			if (!open) return undefined;
			const end = matchElement(src, open[1]);
			if (end < 0) return undefined;
			const lineEnd = src.indexOf("\n", end);
			const stop = lineEnd < 0 ? src.length : lineEnd;
			return { type: "rawHtmlBlock", raw: src.slice(0, stop + (lineEnd < 0 ? 0 : 1)), html: src.slice(0, stop) };
		},
	},
	parseMarkdown: (token: MarkdownToken) => {
		const raw = unprepare(token.html);
		const map = mapArgs(raw);
		if (map) return { type: "odoBox", attrs: { tag: "MAPA", args: map } };
		const table = canonicalTable(raw);
		const html = table === null ? raw : linksToAnchors(table);
		return (table !== null && tableNode(html)) || { type: "rawHtmlBlock", attrs: { raw: html, ial: null } };
	},
	renderMarkdown: (node: JSONContent) => (node.attrs?.ial ? `${node.attrs.raw}\n${node.attrs.ial}` : node.attrs?.raw),
});

// Inline HTML elements and kramdown span IALs on links stay verbatim.
const INLINE_RAW =
	/^(?:<(span|u|a|strong|em|sup|sub)\b[^>]*>|<(img|br)\b[^>]*\/?>|\[!\[[^\]]*\]\([^)]*\)\]\([^)]*\)\{:[^}]*\}|<https?:[^>\s]+>\{:[^}]*\}|&(?!(?:amp|lt|gt|quot|nbsp);)[a-zA-Z][a-zA-Z0-9]*;)/i;
const RawHtmlInline = Node.create({
	name: "rawHtmlInline",
	group: "inline",
	inline: true,
	atom: true,
	addAttributes: () => ({ raw: { default: "", parseHTML: (el: HTMLElement) => el.textContent ?? "" } }),
	parseHTML: () => [{ tag: "span[data-raw-html]" }],
	renderHTML: ({ node }) => ["span", { "data-raw-html": "" }, node.attrs.raw],
	markdownTokenizer: {
		name: "rawHtmlInline",
		level: "inline",
		start: (src: string) => src.search(/<(span|u|a|strong|em|sup|sub|img|br)\b|\[!\[|<https?:|&[a-zA-Z]/i),
		tokenize: (src: string) => {
			const m = INLINE_RAW.exec(src);
			if (!m) return undefined;
			if (!m[1]) return { type: "rawHtmlInline", raw: m[0] };
			const end = matchElement(src, m[1]);
			return end < 0 ? undefined : { type: "rawHtmlInline", raw: src.slice(0, end) };
		},
	},
	parseMarkdown: (token: MarkdownToken) => ({ type: "rawHtmlInline", attrs: { raw: token.raw } }),
	renderMarkdown: (node: JSONContent) => node.attrs.raw,
});

const STAGES = [
	["empty", "Estágio 1: Iniciante (estatísticas para 1º e 2º Níveis)"],
	["half", "Estágio 2: Heroico (estatísticas para 3º a 5º Níveis)"],
	["full", "Estágio 3: Avançado (estatísticas para 6º a 9º Níveis)"],
];

// An image keeps its kramdown span IAL; the stage shield and the short picture path are the short forms.
const IMAGE = /^!\[([^\]\n]*)\]\(([^\s"]+?)(?:(\s+)"([^\n]*?)")?\)(\{:[^}]*\})?/;
const OdoImage = Node.create({
	name: "odoImage",
	group: "inline",
	inline: true,
	atom: true,
	addAttributes: () => ({ src: { default: "" }, alt: { default: "" }, title: { default: null }, gap: { default: " " }, ial: { default: null } }),
	renderHTML: ({ node }) => ["img", { alt: node.attrs.alt, "data-src": node.attrs.src }],
	markdownTokenizer: {
		name: "odoImage",
		level: "inline",
		start: (src: string) => src.indexOf("!["),
		tokenize: (src: string) => {
			const m = IMAGE.exec(src);
			return m ? { type: "odoImage", raw: m[0], alt: m[1], src: m[2], gap: m[3] ?? " ", title: m[4] ?? null, ial: m[5] ?? null } : undefined;
		},
	},
	parseMarkdown: (t: MarkdownToken) => {
		const stage = STAGES.findIndex(
			([fill, label]) => t.src === `ICON_ASSET_PATH[rpg/shield-${fill}.svg]` && t.alt === label && t.title === label && t.ial === '{: class="inline w-4 h-4"}',
		);
		if (stage >= 0) return { type: "odoTag", attrs: { tag: "ESTAGIO", args: String(stage + 1) } };
		// Ruby expands a short path only with one space before the title.
		const asset = t.title !== null && t.gap !== " " ? null : /^CHAPTER_ASSET_PATH\[([\w-]+(?:\/[\w.-]+)*\.\w+)\]$/.exec(t.src);
		return { type: "odoImage", attrs: { src: asset ? asset[1] : t.src, alt: t.alt, title: t.title, gap: t.gap, ial: t.ial } };
	},
	renderMarkdown: (node: JSONContent) => {
		const { src, alt, title, gap, ial } = node.attrs;
		return `![${alt}](${src}${title === null ? "" : `${gap}"${title}"`})${ial ?? ""}`;
	},
});

const Italic = BaseItalic.extend({ renderMarkdown: (node: JSONContent, h: MarkdownRendererHelpers) => `_${h.renderChildren(node)}_` });

const ListItem = BaseListItem.extend({
	addAttributes() {
		return { ...this.parent?.(), spaced: { default: false, rendered: false } };
	},
	renderMarkdown: (node: JSONContent, h: MarkdownRendererHelpers, ctx: RenderContext) =>
		renderNestedMarkdownContent(
			node,
			h,
			(c: RenderContext) => (c.parentType === "orderedList" ? `${(c.meta?.parentAttrs?.start ?? 1) + c.index}. ` : "* "),
			ctx,
		),
});

const looseList = (ext: Node) =>
	ext.extend({
		parseMarkdown(token: MarkdownToken, helpers: MarkdownParseHelpers) {
			const result = this.parent?.(token, helpers);
			if (!result || Array.isArray(result) || !token.items) return result;
			let cursor = 0;
			token.items.forEach((item, i) => {
				const at = token.raw.indexOf(item.raw, cursor);
				cursor = at + item.raw.length;
				const gap = /\s*$/.exec(item.raw)?.[0] + /^\s*/.exec(token.raw.slice(cursor))?.[0];
				const child = result.content?.[i];
				if (child) child.attrs = { ...(child.attrs ?? {}), spaced: (gap.match(/\n/g) ?? []).length >= 2 };
			});
			return result;
		},
		// kramdown wraps an item in <p> by the blank lines around it, item by item.
		renderMarkdown(node: JSONContent, h: MarkdownRendererHelpers) {
			return (node.content ?? []).map((item, i) => (i > 0 ? (node.content?.[i - 1].attrs?.spaced ? "\n\n" : "\n") : "") + h.renderChild?.(item, i)).join("");
		},
	});

// A hard break is a newline inside a paragraph, as hard_wrap reads it.
const HardBreak = Node.create({
	name: "hardBreak",
	group: "inline",
	inline: true,
	selectable: false,
	parseHTML: () => [{ tag: "br" }],
	renderHTML: () => ["br"],
	markdownTokenName: "br",
	parseMarkdown: () => ({ type: "hardBreak" }),
	renderMarkdown: () => "\n",
});

export const extensions = [
	StarterKit.configure({
		italic: false,
		listItem: false,
		bulletList: false,
		orderedList: false,
		link: false,
		underline: false,
		strike: false,
		hardBreak: false,
		code: false,
		codeBlock: false,
		horizontalRule: false,
		trailingNode: false,
		heading: false,
		paragraph: false,
		blockquote: false,
		dropcursor: false,
		gapcursor: false,
		listKeymap: false,
		undoRedo: false,
	}),
	withIal(Paragraph),
	withIal(Heading.configure({ levels: [1, 2, 3, 4, 5, 6] })),
	withContainer(withIal(Blockquote)),
	Callout,
	withIal(looseList(BaseBulletList)),
	withIal(looseList(BaseOrderedList)),
	ListItem,
	Italic,
	Link.extend({ priority: 50 }).configure({ openOnClick: false, autolink: false, linkOnPaste: false }),
	HardBreak,
	OdoTag,
	OdoBox,
	OdoImage,
	RawHtmlBlock,
	RawHtmlInline,
	BlockIal,
	...tableExtensions,
];

const INLINE_ATOMS = new Set(["odoTag", "odoImage", "rawHtmlInline"]);
// Token types that become verbatim nodes: nothing here is a node the editor knows.
const RAW_BLOCKS = new Set(["code", "hr", "table", "html", "def"]);
const ATOM_OPEN = String.fromCharCode(0xe000);
const ATOM_CLOSE = String.fromCharCode(0xe001);
const NBSP = new RegExp(String.fromCharCode(0xa0), "g");
const ATOM = new RegExp(`${ATOM_OPEN}(\\d+)${ATOM_CLOSE}`, "g");

// The stock manager escapes `[` and `_` everywhere, drops marks that start on an atom and opens equal marks
// by one node of lookahead; its hooks are private, so the subclass is typed loosely.
interface StockManager {
	activeParseLexer: unknown;
	createLexer(): { lex(markdown: string): MarkdownToken[]; inlineTokens(src: string): MarkdownToken[] };
	parseTokens(tokens: MarkdownToken[], inline: boolean): JSONContent[];
	parseInlineTokens(tokens: MarkdownToken[]): JSONContent[];
	parseFallbackToken(token: MarkdownToken, implicitParagraphs?: boolean): JSONContent | JSONContent[] | null;
	getHandlerForToken(type: string): unknown;
	applyMarkToContent(markType: string, content: JSONContent[], attrs?: object): JSONContent[];
	renderNodeToMarkdown(node: JSONContent, parent: JSONContent | null): string;
	renderNodesWithMarkBoundaries(nodes: JSONContent[], parent: JSONContent | undefined, separator?: string, level?: number): string;
	getMarksToOpenForSerialization(
		active: Map<string, JSONContent>,
		current: Map<string, JSONContent>,
		next: JSONContent | null,
	): { type: string; mark: JSONContent }[];
	serialize(doc: JSONContent): string;
}
const Base = MarkdownManager as unknown as new (options: object) => StockManager;

class OdoMarkdownManager extends Base {
	siblings: JSONContent[] = [];

	parse(markdown: string) {
		const lexer = this.createLexer();
		this.activeParseLexer = lexer;
		try {
			return restoreColons({ type: "doc", content: attachIals(this.parseTokens(lexer.lex(prepare(markdown)), false)) });
		} finally {
			this.activeParseLexer = null;
		}
	}

	// A token no extension reads comes back as it was written, never dropped or reinterpreted.
	parseFallbackToken(token: MarkdownToken, implicitParagraphs?: boolean) {
		if (token.type === "image") return { type: "paragraph", content: [{ type: "rawHtmlInline", attrs: { raw: token.raw } }] };
		if (token.type && RAW_BLOCKS.has(token.type)) return { type: "rawHtmlBlock", attrs: { raw: unprepare((token.raw ?? "").replace(/\n+$/, "")), ial: null } };
		return super.parseFallbackToken(token, implicitParagraphs);
	}

	// What no extension reads stays as written; `&nbsp;` is read as the character it draws, as the stock decoder reads `&amp;`.
	parseInlineTokens(tokens: MarkdownToken[]) {
		const read = (token: MarkdownToken) => {
			if (token.type === "text") return { ...token, text: (token.text ?? "").replace(/&nbsp;/g, String.fromCharCode(0xa0)) };
			if (!token.type || token.type === "escape") return token;
			return token.type === "html" || !this.getHandlerForToken(token.type) ? { type: "rawHtmlInline", raw: token.raw } : token;
		};
		return super.parseInlineTokens(tokens.map(read));
	}

	applyMarkToContent(markType: string, content: JSONContent[], attrs?: object) {
		return content.map((node) =>
			INLINE_ATOMS.has(node.type)
				? { ...node, marks: [...(node.marks ?? []), attrs ? { type: markType, attrs } : { type: markType }] }
				: super.applyMarkToContent(markType, [node], attrs)[0],
		);
	}

	renderNodesWithMarkBoundaries(nodes: JSONContent[], parent: JSONContent | undefined, separator?: string, level?: number) {
		const outer = this.siblings;
		this.siblings = nodes;
		try {
			return super.renderNodesWithMarkBoundaries(nodes, parent, separator, level);
		} finally {
			this.siblings = outer;
		}
	}

	// Marks that open together nest by how long they stay open, the longer outside, so overlapping marks stay well-formed.
	getMarksToOpenForSerialization(active: Map<string, JSONContent>, current: Map<string, JSONContent>, next: JSONContent | null) {
		const opening = super.getMarksToOpenForSerialization(active, current, next);
		if (opening.length < 2) return opening;
		const at = next ? this.siblings.indexOf(next) - 1 : this.siblings.length - 1;
		const span = ({ type, mark }: { type: string; mark: JSONContent }) => {
			let end = at;
			while (this.siblings[end + 1]?.marks?.some((m) => m.type === type && attrsEqual(m.attrs, mark.attrs))) end += 1;
			return end;
		};
		return opening
			.map((entry) => ({ entry, end: span(entry) }))
			.sort((a, b) => a.end - b.end)
			.map(({ entry }) => entry);
	}

	// Inline atoms become placeholder text carrying their marks, so `**MONSTRO[x]**` keeps its bold: the
	// serializer only opens marks on text nodes. A map or a short box inside a quote or list stays long,
	// since Markdown::Notation reads `MAPA[…]` and `:::` only at the start of a line.
	serialize(doc: JSONContent) {
		const atoms: string[] = [];
		// quoted: inside any box, quote or list; plain: inside a quote or list that is not a short box.
		const swap = (node: JSONContent, quoted: boolean, plain: boolean): JSONContent => {
			// A table writes its own cells.
			if (node.type === "table") return node;
			if (INLINE_ATOMS.has(node.type)) {
				atoms.push(this.renderNodeToMarkdown(node, null));
				return { type: "text", text: `${ATOM_OPEN}${atoms.length - 1}${ATOM_CLOSE}`, marks: node.marks };
			}
			if (quoted && node.type === "odoBox" && node.attrs?.tag === "MAPA") {
				const html = expandMap(node.attrs.args);
				return html ? { type: "rawHtmlBlock", attrs: { raw: html } } : node;
			}
			const ial = node.type === "blockquote" ? boxIal(node.attrs?.ial) : null;
			const box = !plain && ial !== null && shortNames(ial[1]) !== null;
			const container = node.type === "blockquote" || node.type === "listItem";
			const attrs = plain && node.type === "blockquote" ? { ...node.attrs, long: true } : node.attrs;
			return node.content ? { ...node, attrs, content: node.content.map((child) => swap(child, quoted || container, plain || (container && !box))) } : node;
		};
		const out = super.serialize(swap(doc, false, false)).replace(ATOM, (_: string, i: string) => atoms[Number(i)]);
		return out ? `${contractTests(out.replace(/\n+$/, ""))}\n` : "";
	}

	encodeTextForMarkdown(text: string, node: JSONContent, parent: JSONContent | undefined) {
		const linked = node.marks?.some((mark) => mark.type === "link");
		let out = text
			.replace(/\\/g, "\\\\")
			.replace(/&(?=(?:[a-zA-Z][a-zA-Z0-9]*|#\d+|#[xX][0-9a-fA-F]+);)/g, "&amp;")
			.replace(/<(?=[a-zA-Z/!])/g, "&lt;")
			.replace(NBSP, "&nbsp;")
			.replace(/([`*|])/g, "\\$1")
			.replace(/_+/g, (run, i, all) =>
				/[\p{L}\p{N}]/u.test(all[i - 1] ?? "") && /[\p{L}\p{N}]/u.test(all[i + run.length] ?? "") ? run : run.replace(/_/g, "\\_"),
			);
		const reads = (tokens: MarkdownToken[]): boolean => tokens.some((t) => t.type === "link" || t.type === "image" || (t.tokens ? reads(t.tokens) : false));
		if (linked) out = out.replace(/[[\]]/g, "\\$&");
		else if (out.includes("[") && reads(this.createLexer().inlineTokens(out))) out = out.replace(/\[/g, "\\[");
		else out = out.replace(/\[(?=[^\]]*$)/g, "\\[");
		out = out.replace(/(ESTAGIO|MAPA)\\?\[/g, "$1\\[");
		const at = parent?.content?.indexOf(node) ?? 0;
		if (parent?.type === "paragraph" && (at <= 0 || parent.content?.[at - 1]?.type === "hardBreak")) {
			out = out
				.replace(/^( {0,3})([-+])(?= |\t|$)/, "$1\\$2")
				.replace(/^( {0,3})(\d+)([.)])(?= |$)/, "$1$2\\$3")
				.replace(/^( {0,3})(#|>|:::|\{:|~~~|-{3,})/, "$1\\$2")
				.replace(/^( {0,3})(=+)$/, "$1\\$2");
		}
		if (parent?.type === "heading" && at === (parent.content?.length ?? 0) - 1) out = out.replace(/(^|\s)(#+)$/, "$1\\$2");
		return out;
	}
}

export function createManager() {
	return new OdoMarkdownManager({ extensions, markedOptions: { gfm: true, breaks: true } });
}

export function roundTrip(markdown: string, manager = createManager()): string {
	return manager.serialize(manager.parse(markdown));
}
